<script lang="ts" module>
  import type { AiSuggestion } from '@/teoCore/TeoApi';
  import SmallGhostButton from '@/ui/elements/buttons/SmallGhostButton.svelte';
  import ExpandableCard, {
    type ExpandableCardProps,
  } from '@/ui/elements/card/ExpandableCard.svelte';
  import Checkbox from '@/ui/elements/checkboxcomp/Checkbox.svelte';
  import BrandFitResult, {
    type Variant as BrandFitResultVariant,
  } from '@/ui/elements/pills/BrandFitResult.svelte';
  import Improvement, { type ImprovementProps } from '@/ui/elements/pills/Improvement.svelte';

  export type AssistantVariant = 'wording' | 'phrases' | 'recommendation' | 'spelling' | 'grammar';

  export interface AssistantCardProps {
    key: string;
    cardLabel: string;
    open?: boolean;
    variantLabel: string;
    variant: AssistantVariant;
    contentText?: string;
    improvementsLabel?: string;
    useAccordion?: ExpandableCardProps['useAccordion'];
    infoText?: string;
    onInfoClick?: () => void;
    checkedText?: string;
    onCheckedClick?: (entryKey: string, checked: boolean) => void;
    improvements: Omit<ImprovementProps, 'onclick'>[];
    onImprovementClick?: (improvementKey: string) => void;
    aiSuggestions?: AiSuggestion[];
    onLoadAiSuggestionClick?: () => Promise<void>;
  }
</script>

<script lang="ts">
  let { ...props }: AssistantCardProps = $props();

  let brandFitResultVariant: BrandFitResultVariant = props.variant;
</script>

<ExpandableCard useAccordion={props.useAccordion} expanded={props.open}>
  {#snippet header()}
    <div class="flex w-full items-center py-1">
      <p class="flex-1 text-left align-middle font-sans text-sm font-normal text-c">
        {props.cardLabel}:
      </p>
      <BrandFitResult variant={brandFitResultVariant}>{props.variantLabel}</BrandFitResult>
    </div>
  {/snippet}
  {#snippet content()}
    <div class="grid gap-2 py-2">
      {#if props.contentText || props.improvements.length}
        <hr class="border-t-c-divider" />
        <div class="grid gap-3">
          {#if props.contentText}
            <p class="font-sans text-sm font-normal text-c-base">{props.contentText}</p>
          {/if}
          {#if props.improvements.length}
            {#if props.improvementsLabel}
              <p class="font-sans text-sm font-normal text-c-base">{props.improvementsLabel}</p>
            {/if}
            {#each props.improvements as improvement (improvement.key)}
              <Improvement
                onclick={(improvementKey) => {
                  props.onImprovementClick?.(improvementKey);
                }}
                {...improvement}
              />
            {/each}
          {/if}
        </div>
      {/if}
      {#if props.checkedText}
        <div class="grid gap-2">
          <hr class="border-t-c-divider" />
          <Checkbox
            item={{ id: 0, label: props.checkedText, checked: false }}
            onChecked={(item) => {
              props.onCheckedClick?.(props.key, item.checked ?? false);
            }}
          ></Checkbox>
        </div>
      {/if}
      {#if props.infoText}
        <hr class="border-t-c-divider" />
        <div class="text-right">
          <SmallGhostButton
            icon="InfoOutline"
            label={props.infoText}
            onclick={() => {
              props.onInfoClick?.();
            }}
          ></SmallGhostButton>
        </div>
      {/if}
    </div>
  {/snippet}
</ExpandableCard>
